import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useMountingInventoryAPI } from '@elfsight/widget-mounting-inventory';
import { Shadow, useShadowAPI } from '@elfsight/widget-shadow';
import { createPortal } from 'react-dom';
import { usePortalNestProvider } from './portal-nest-provider';

export interface PortalProps {
  children: ReactNode;
  containerElement?: Element;
  shadow?: boolean;
}

export function Portal({ children, containerElement, shadow }: PortalProps) {
  const nest = usePortalNestProvider();
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (isMountedRef.current && !(nest || containerElement)) {
      console.warn(
        `containerElement for Portal fallback to "document.body" that can occur a problems with custom CSS. Is it wrapped with PortalNestProvider?`
      );
    }

    isMountedRef.current = true;
  }, [nest]);

  const finalContainer = containerElement ?? nest ?? document.body;

  const isInShadow = !!useShadowAPI();
  const shouldShadowImplicitly = isInShadow && finalContainer === nest;
  const finalShadow = shadow ?? shouldShadowImplicitly;

  const mountingInventoryAPI = useMountingInventoryAPI();

  useEffect(() => {
    if (mountingInventoryAPI && !finalShadow) {
      // in case it's in a Shadow,
      // the Shadow will add it's root itself
      return mountingInventoryAPI.addRoot(finalContainer);
    }

    return undefined;
  }, [mountingInventoryAPI, finalContainer, finalShadow]);

  return createPortal(
    finalShadow ? <Shadow>{children}</Shadow> : children,
    finalContainer
  );
}
